import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../../../components/Button";
import ConfirmModal from "../../../../../../components/ConfirmModal";
import Input from "../../../../../../components/Form/Input/input";
import { Select } from "../../../../../../components/Form/Input/select";
import Label from "../../../../../../components/Form/Label";
import { FormGroup } from "../../../../../../components/Form/styles";
import { GlobeIcon } from "../../../../../../icons/Globe";
import { DOMAIN_PROVIDERS } from "../../../../../../model/domainProviders";
import { isValidDomain, normalizeDomain } from "../../../../../../services/customDomain";
import { getMyDomains, keepoConnectErrorKey, OwnedDomain, searchDomains } from "../../../../../../services/domains";
import { formatMoney } from "../../../../../../utils/money";
import { SmallButton, Tag } from "../../../../../Domains/styles";
import { Card, CardFooter, FreeDomainOffer, Section } from "../../styles";
import { FreeDomainModal } from "../FreeDomainModal";
import { KeepoDomains } from "../KeepoDomains";

// How long after typing a domain it's checked (to offer it when nobody registered it yet)
const AVAILABILITY_DELAY = 600;

type Props = {
  /** This Keepo (a domain bought at Keepo on another one is moved here). */
  siteId: string;
  onConnect: (domain: string, provider: string) => Promise<void>;
  /** Connects a domain bought at Keepo; throws when it can't (shown in the modal). */
  onConnectKeepoDomain: (domain: string) => Promise<void>;
};

/**
 * First step, two ways: a domain bought at Keepo (picked in a modal, Keepo sets its DNS by itself), or one from
 * another provider (the domain and where it was bought, then its DNS records).
 */
export const AddDomain = ({ siteId, onConnect, onConnectKeepoDomain }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // The domains bought at Keepo that can be connected (registered); undefined while loading
  const [keepoDomains, setKeepoDomains] = useState<OwnedDomain[]>();
  // The domain bought at Keepo being connected now
  const [connectingKeepo, setConnectingKeepo] = useState<string>();
  const [keepoError, setKeepoError] = useState<string>();

  useEffect(() => {
    getMyDomains()
      .then(list => setKeepoDomains(list.filter(item => item.status === "active" || item.status === "expiring")))
      .catch(() => setKeepoDomains([]));
  }, []);

  const connectKeepoDomain = async (value: string) => {
    setConnectingKeepo(value);
    setKeepoError(undefined);
    try {
      await onConnectKeepoDomain(value);
    } catch (error) {
      setKeepoError(t(`pages.settings.customDomain.keepoDomain.errors.${keepoConnectErrorKey(error)}`));
    } finally {
      setConnectingKeepo(undefined);
    }
  };
  const [domain, setDomain] = useState("");
  const [provider, setProvider] = useState("");
  const [touched, setTouched] = useState(false);
  const [isConnecting, setConnecting] = useState(false);

  const prefix = "pages.settings.customDomain.add";
  const normalized = normalizeDomain(domain);
  const isDomainValid = isValidDomain(normalized);
  // A domain typed for another provider that nobody registered yet: offered at Keepo instead (connecting it
  // would never work)
  const [freeOffer, setFreeOffer] = useState<{ domain: string; price: number }>();
  // The free domain whose modal was closed: it doesn't open again for it (the offer stays in the form)
  const [dismissedOffer, setDismissedOffer] = useState<string>();
  const registerFree = (value: string) =>
    navigate(`/domains/checkout?domain=${encodeURIComponent(value)}&years=1&site=${encodeURIComponent(siteId)}`);
  const canConnect = isDomainValid && !!provider && !isConnecting && !freeOffer;

  useEffect(() => {
    setFreeOffer(undefined);
    // Only name.tld: not a subdomain, nor a .com.br-like extension (not sold at Keepo)
    if (!isDomainValid || normalized.split(".").length !== 2) {
      return;
    }
    let isCurrent = true;
    const timer = setTimeout(() => {
      searchDomains(normalized)
        .then(({ exact }) => {
          if (isCurrent && exact.available && exact.price && !exact.premium && !exact.notSold) {
            setFreeOffer({ domain: exact.domain, price: exact.price.register });
          }
        })
        // Couldn't check (e.g. too many searches): the form just works as usual
        .catch(() => undefined);
    }, AVAILABILITY_DELAY);
    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [normalized, isDomainValid]);

  const submit = async () => {
    setTouched(true);
    if (!canConnect) {
      return;
    }
    setConnecting(true);
    try {
      await onConnect(normalized, provider);
    } finally {
      setConnecting(false);
    }
  };

  return (
    <>
      {/* The domains bought at Keepo, right in the box: one click connects one, Keepo does the rest */}
      <Section>
        <KeepoDomains
          domains={keepoDomains}
          siteId={siteId}
          connecting={connectingKeepo}
          onConnect={connectKeepoDomain}
          onBuy={() => navigate("/domains")}
        />
      </Section>
      {/* Couldn't connect it: in a modal, as the page's other errors */}
      {keepoError && (
        <ConfirmModal
          type="INFO"
          title={t("pages.settings.customDomain.errors.title")}
          descriptions={[keepoError]}
          confirmButtonTitle={t("pages.settings.customDomain.errors.ok")}
          onConfirm={() => setKeepoError(undefined)}
          onExit={() => setKeepoError(undefined)}
        />
      )}

      {/* A domain from another provider: always open, under the ones bought at Keepo */}
      <Section>
        <Card
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            submit();
          }}>
          <h3>{t("pages.settings.customDomain.keepoDomain.otherProvider")}</h3>
          <p className="lead">{t(`${prefix}.intro`)}</p>
          {/* Found free: said in a modal too, once per domain */}
          {freeOffer && freeOffer.domain !== dismissedOffer && (
            <FreeDomainModal
              domain={freeOffer.domain}
              price={formatMoney(Math.round(freeOffer.price * 100), "brl", i18n.language)}
              onRegister={() => registerFree(freeOffer.domain)}
              onClose={() => setDismissedOffer(freeOffer.domain)}
            />
          )}
          {/* Typed a domain nobody registered: offered at Keepo, right above the field */}
          {freeOffer && (
            <FreeDomainOffer role="note">
              <span className="icon" aria-hidden="true">
                <GlobeIcon size={18} />
              </span>
              <span className="text">
                <span className="name">
                  <b>{freeOffer.domain}</b>
                  <Tag $tone="plus">{t("pages.settings.customDomain.keepoDomain.offer.available")}</Tag>
                </span>
                <span className="detail">{t("pages.settings.customDomain.keepoDomain.offer.text")}</span>
              </span>
              <span className="buy">
                <span className="price">
                  {t("pages.settings.customDomain.keepoDomain.offer.perYear", {
                    value: formatMoney(Math.round(freeOffer.price * 100), "brl", i18n.language),
                  })}
                </span>
                <SmallButton
                  type="button"
                  $primary
                  aria-label={t("pages.settings.customDomain.keepoDomain.offer.ctaFor", { domain: freeOffer.domain })}
                  onClick={() => registerFree(freeOffer.domain)}>
                  {t("pages.settings.customDomain.keepoDomain.offer.cta")}
                </SmallButton>
              </span>
            </FreeDomainOffer>
          )}
          <FormGroup>
            <Input
              id="domain"
              type="text"
              value={domain}
              label={t(`${prefix}.domain`)}
              placeholder={t(`${prefix}.domainPlaceholder`)}
              autoCapitalize="none"
              ignoreLengthInfo
              maxLength={253}
              onChange={e => setDomain(e.target.value)}
              infoMessage={touched && domain && !isDomainValid ? t(`${prefix}.invalid`) : ""}
            />
          </FormGroup>
          <FormGroup>
            <Select id="provider" value={provider} onChange={e => setProvider(e.target.value)}>
              <option value="" disabled>
                {t(`${prefix}.providerSelect`)}
              </option>
              {DOMAIN_PROVIDERS.map(item => (
                <option key={item.key} value={item.key}>
                  {item.key === "other" ? t(`${prefix}.otherProvider`) : item.name}
                </option>
              ))}
            </Select>
            <Label htmlFor="provider" text={t(`${prefix}.provider`)} />
          </FormGroup>
          <CardFooter>
            <Button
              type="INFO"
              size="MD"
              disabled={!canConnect}
              $loading={isConnecting}
              aria-busy={isConnecting}
              onClick={submit}>
              {/* While connecting, only Keepo's loading shows (the label stays underneath, same width) */}
              {t(`${prefix}.submit`)}
            </Button>
          </CardFooter>
        </Card>
      </Section>
    </>
  );
};
